/* eslint-disable @typescript-eslint/naming-convention */
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"
import { client, sdk } from "@/api/sdk"
import { queryClient } from "./client"
import { setAuthToken } from "@/stores/auth"
import { reset as analyticsReset } from "@/analytics"
import { oneSignalLogout } from "@/stores/oneSignal"
import { logoutSentryUser } from "@/hooks/sentry"
import { resetRefCode } from "@/stores/refCode"
import { resetUtm } from "@/stores/utm"
import { identifyUser } from "@/services/IdentifyService"
import { resetSubjectPropertyWalkthrough } from "@/stores/walkThroughs"
import { clearPreQualState } from "@/stores/preQual"
import { $ldKey, getLDClient } from "@/services/launchDarkly"
import { expoUnregisterDevice } from "@/stores/expoPushNotifications"

export const userQueryOptions = queryOptions({
  queryKey: ["user"],
  queryFn: async () => {
    const queryResp = await sdk.Me()
    const userProfile = queryResp.me

    await identifyUser(userProfile)
    return queryResp
  },

  staleTime: 5000,
  gcTime: Infinity,
})

export type UserProfileType = NonNullable<
  Awaited<ReturnType<typeof sdk.Me>>["me"]
>

export const useUser = () => {
  return useQuery(userQueryOptions)
}

type CheckAccountExistsPayload = Parameters<typeof sdk.checkAccountExists>[0]

export const useCheckAccountExists = () => {
  return useMutation({
    mutationFn: async (payload: CheckAccountExistsPayload) => {
      const queryResp = await sdk.checkAccountExists(payload)
      const accountExists = queryResp.accountExists?.exists || false
      return accountExists
    },
  })
}

export const useLoginMutation = () => {
  return useMutation({
    mutationFn: async (loginPayload: {
      username: string
      password: string
    }) => {
      return await sdk.UsernamePasswordLogin(loginPayload)
    },
    onSuccess: async (data) => {
      const authToken = data?.login?.token ?? null
      setAuthToken(authToken)
      if (authToken) {
        client.setHeader(`authorization`, `jwt ${authToken}`)
      }
      await queryClient.invalidateQueries()
    },
  })
}

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: async (resetPasswordPayload: { email: string }) => {
      return await sdk.UserResetPasswordRequest(resetPasswordPayload)
    },
    onSuccess: async (data) => {
      console.log(data)
    },
  })
}

export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: async (changePasswordPayload: {
      token: string
      password: string
    }) => {
      return await sdk.UserChangePasswordRequest(changePasswordPayload)
    },
    onSuccess: async (data) => {
      console.log(data)
    },
  })
}

type RegisterPayload = Parameters<typeof sdk.Registration>[0]

export const useRegistrationMutation = () => {
  return useMutation({
    mutationFn: async (registerPayload: RegisterPayload) => {
      return await sdk.Registration(registerPayload)
    },
    onSuccess: async (data) => {
      const authToken = data?.register?.token || null
      setAuthToken(authToken)
      if (authToken) {
        client.setHeader(`authorization`, `jwt ${authToken}`)
      }
      await queryClient.invalidateQueries()
    },
  })
}

export const useLogout = () => {
  const logout = async () => {
    try {
      void expoUnregisterDevice()
      setAuthToken(null)
      oneSignalLogout()
      void analyticsReset()
      logoutSentryUser()
      resetRefCode()
      resetUtm()
      clearPreQualState()
      resetSubjectPropertyWalkthrough()
      // EN-4932
      // Uninitialize LaunchDarkly
      const ldClient = getLDClient()
      if (ldClient) {
        await ldClient.flush() // Ensure all events are sent
        await ldClient.close() // Shut down LaunchDarkly client
      }
      $ldKey.set(null) // Reset stored LD key

      await queryClient.invalidateQueries()
    } catch (error) {
      console.error("Failed to logout:", error)
    }
  }
  return { logout }
}

type GoogleLoginAuthPayload = Parameters<typeof sdk.GoogleLoginAuth>[0] & {
  serviceType: "auth"
}

export const useGoogleLoginAuthMutation = () => {
  return useMutation({
    mutationFn: async (googleLoginAuthPayload: GoogleLoginAuthPayload) => {
      return await sdk.GoogleLoginAuth(googleLoginAuthPayload)
    },
    onSuccess: async (data) => {
      const authToken = data?.googleLogin?.token || null
      setAuthToken(authToken)
      if (authToken) {
        client.setHeader(`authorization`, `jwt ${authToken}`)
      }
      await queryClient.invalidateQueries()
    },
  })
}

type GoogleLoginRegisterPayload = Parameters<
  typeof sdk.GoogleLoginRegister
>[0] & {
  serviceType: "register"
}

export const useGoogleLoginRegisterMutation = () => {
  return useMutation({
    mutationFn: async (
      googleLoginRegisterPayload: GoogleLoginRegisterPayload
    ) => {
      return await sdk.GoogleLoginRegister(googleLoginRegisterPayload)
    },
    onSuccess: async (data) => {
      setAuthToken(data?.googleLogin?.token || null)
      await queryClient.invalidateQueries()
    },
  })
}

type AppleLoginAuthPayload = Parameters<typeof sdk.AppleLoginAuth>[0] & {
  serviceType: "auth"
}

export const useAppleLoginAuthMutation = () => {
  return useMutation({
    mutationFn: async (appleLoginAuthPayload: AppleLoginAuthPayload) => {
      return await sdk.AppleLoginAuth(appleLoginAuthPayload)
    },
    onSuccess: async (data) => {
      const authToken = data?.appleLogin?.token || null
      setAuthToken(authToken)
      if (authToken) {
        client.setHeader(`authorization`, `jwt ${authToken}`)
      }
      await queryClient.invalidateQueries()
    },
  })
}

type AppleLoginRegisterPayload = Parameters<
  typeof sdk.AppleLoginRegister
>[0] & {
  serviceType: "register"
}

export const useAppleLoginRegisterMutation = () => {
  return useMutation({
    mutationFn: async (
      appleLoginRegisterPayload: AppleLoginRegisterPayload
    ) => {
      return await sdk.AppleLoginRegister(appleLoginRegisterPayload)
    },
    onSuccess: async (data) => {
      const authToken = data?.appleLogin?.token || null
      setAuthToken(authToken)
      if (authToken) {
        client.setHeader(`authorization`, `jwt ${authToken}`)
      }
      await queryClient.invalidateQueries()
    },
  })
}

export const useUserProfileNameMutation = () => {
  return useMutation({
    mutationFn: async (updateUserProfilePayload: {
      firstName: string
      lastName: string
    }) => {
      const resp = await sdk.updateUserProfileName(updateUserProfilePayload)
      const resultType = resp.updateUserProfile?.result?.__typename
      if (typeof resultType === "undefined") {
        // this shouldn't happen
        throw Error("Invalid Graphql Response.")
      }

      if (resp.updateUserProfile?.result) {
        return resp.updateUserProfile.result
      }
    },
    onSuccess: async (data) => {
      if (!data) return

      const { __typename } = data

      if (__typename === "UserProfileUpdateSuccess") {
        // eslint-disable-next-line @typescript-eslint/return-await
        return queryClient.invalidateQueries({
          queryKey: ["user"],
        })
      }
    },
  })
}

export const useUserProfilePhoneNumberMutation = () => {
  return useMutation({
    mutationFn: async (updateUserProfilePayload: { phoneNumber: string }) => {
      const resp = await sdk.updateUserProfilePhoneNumber(
        updateUserProfilePayload
      )
      const resultType = resp.updateUserProfile?.result?.__typename
      if (typeof resultType === "undefined") {
        throw Error("Invalid Graphql Response.")
      }

      if (resp.updateUserProfile?.result) {
        return resp.updateUserProfile.result
      }
    },
    onSuccess: async (data) => {
      if (!data) return

      const { __typename } = data

      if (__typename === "UserProfileUpdateSuccess") {
        // eslint-disable-next-line @typescript-eslint/return-await
        return queryClient.invalidateQueries({ queryKey: ["user"] })
      }
    },
  })
}

export const useUserProfileEmailMutation = () => {
  return useMutation({
    mutationFn: async (updateUserProfilePayload: { email: string }) => {
      const resp = await sdk.updateUserProfileEmail(updateUserProfilePayload)
      const resultType = resp.updateUserProfile?.result?.__typename
      if (typeof resultType === "undefined") {
        // this shouldn't happen
        throw Error("Invalid Graphql Response.")
      }

      if (resp.updateUserProfile?.result) {
        return resp.updateUserProfile.result
      }
    },
    onSuccess: async (data) => {
      if (!data) return

      const { __typename } = data

      if (__typename === "UserProfileUpdateSuccess") {
        const newToken = data.token
        if (newToken) {
          setAuthToken(newToken)
        }
        // eslint-disable-next-line @typescript-eslint/return-await
        return queryClient.invalidateQueries({ queryKey: ["user"] })
      }
    },
  })
}

export const useUserProfileContactEmailMutation = () => {
  return useMutation({
    mutationFn: async (updateUserProfilePayload: { contactEmail: string }) => {
      const resp = await sdk.updateUserProfileContactEmail(
        updateUserProfilePayload
      )
      const resultType = resp.updateUserProfile?.result?.__typename
      if (typeof resultType === "undefined") {
        // this shouldn't happen
        throw Error("Invalid Graphql Response.")
      }

      if (resp.updateUserProfile?.result) {
        return resp.updateUserProfile.result
      }
    },
    onSuccess: async (data) => {
      if (!data) return

      const { __typename } = data

      if (__typename === "UserProfileUpdateSuccess") {
        const newToken = data.token
        if (newToken) {
          setAuthToken(newToken)
        }
        // console.log(`* data - ${JSON.stringify(data)}`)
        // eslint-disable-next-line @typescript-eslint/return-await
        await queryClient.invalidateQueries({ queryKey: ["user"] })
      }
    },
    onError: (error) => {
      console.error("Error updating contact email:", error)
    },
  })
}

export const useDeleteUserAccountMutation = () => {
  return useMutation({
    mutationFn: async (deleteUserAccountPayload: { userId: string }) => {
      const resp = await sdk.deleteUserAccount(deleteUserAccountPayload)
      const isSuccess = resp.deleteAccount?.result?.success || false

      if (isSuccess) {
        return isSuccess || false
      }
    },
    onSuccess: async () => {
      // eslint-disable-next-line @typescript-eslint/return-await
      return queryClient.invalidateQueries()
    },
  })
}

export const useUserProfileAttributionMutation = () => {
  return useMutation({
    mutationFn: async (updateUserProfilePayload: {
      attribution: string
      attributionExtra?: string
    }) => {
      const resp = await sdk.updateUserProfileAttribution(
        updateUserProfilePayload
      )
      const resultType = resp.updateUserProfile?.result?.__typename
      if (typeof resultType === "undefined") {
        throw Error("Invalid Graphql Response.")
      }

      if (resp.updateUserProfile?.result) {
        return resp.updateUserProfile.result
      }
    },
    onSuccess: async (data) => {
      if (!data) return

      const { __typename } = data

      if (__typename === "UserProfileUpdateSuccess") {
        // eslint-disable-next-line @typescript-eslint/return-await
        return queryClient.invalidateQueries({ queryKey: ["user"] })
      }
    },
  })
}

export const useRewardsOptInMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const resp = await sdk.UpdateRewardsOptIn()
      return resp.rewardsOptIn
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user"] })
    },
  })
}
